/**
 * Shared large UPS list helpers — grouping and table cell display (#996 Phase 4).
 * Mirrors coolingSystemListUtils.
 *
 * Phase 2.5: the section-agnostic core (grouping, reorder, session patch,
 * duplicate naming, validation, draft, legacy synthesis) comes from the
 * shared repeatable-section factory. Large UPS-specific table column display
 * stays here.
 */

import { DEFAULT_LARGE_UPS_CONFIGURATION } from '../../types/largeUps';
import {
  generateAutoLargeUpsName,
  isAutoGeneratedLargeUpsName,
} from '../../utils/largeUpsAutoName';
import {
  buildRepeatableNameSubline,
  filterRepeatableLocationTagTableColumns,
  formatRepeatableLocationTagColumnHeader,
  isNativeCompactLayout,
  repeatableNameColumnUsesDoubleLine,
  type RepeatableLocationTagColumnOptions,
} from '../../utils/locationTagListDisplay';
import {
  createRepeatableSectionListUtils,
  parseOptionalNumber as parseOptionalNumberCore,
} from '../repeatable/repeatableSectionListUtils';

import type {
  LargeUpsConfiguration,
  GroupedLargeUpsConfigurations,
  UpdateLargeUpsConfigurationInput,
} from '../../types/largeUps';

/** Desktop web columns (#996). MODEL carries the descriptive copy (battery subline). */
export const LARGE_UPS_TABLE_DESKTOP_COLUMNS = [
  'NAME',
  'MODEL',
  'RUNTIME',
  'REDUNDANCY',
  'EXISTING',
  'ROOM',
] as const;

/**
 * Compact mirrors desktop column order; REDUNDANCY folds away on narrow screens.
 * ROOM (#999 Story 3.4) stays on compact so the location tag stays visible.
 * Desktop: NAME | MODEL | RUNTIME | REDUNDANCY | EXISTING | ROOM
 * Compact: NAME (+redundancy) | MODEL | EXISTING | ROOM
 */
export const LARGE_UPS_TABLE_COMPACT_COLUMNS = [
  'NAME',
  'MODEL',
  'EXISTING',
  'ROOM',
] as const;

/**
 * Compact skeleton (#999 Option B) — uniform 4-column layout shared by the compact
 * layout on native mobile AND narrow/mobile web. EXISTING is dropped (it collides
 * with the EXISTING/NEW status pill) and replaced by the battery RUNTIME metric;
 * redundancy relocates under RUNTIME. Wide desktop web keeps
 * {@link LARGE_UPS_TABLE_DESKTOP_COLUMNS}; {@link LARGE_UPS_TABLE_COMPACT_COLUMNS}
 * is retained only for the legacy type union.
 * Compact: NAME (+pill) | MODEL (+Battery:) | RUNTIME (+redundancy) | ROOM
 */
export const LARGE_UPS_TABLE_NATIVE_COMPACT_COLUMNS = [
  'NAME',
  'MODEL',
  'RUNTIME',
  'ROOM',
] as const;

/** All column keys (superset for typing and legacy imports). */
export const LARGE_UPS_TABLE_COLUMNS = LARGE_UPS_TABLE_DESKTOP_COLUMNS;

export type LargeUpsTableLayout = 'desktop' | 'compact';

/** Injected only when `showStatusColumn` is set (Devices view). */
export const LARGE_UPS_TABLE_STATUS_COLUMN = 'STATUS' as const;

export type LargeUpsTableColumnKey =
  | (typeof LARGE_UPS_TABLE_DESKTOP_COLUMNS)[number]
  | (typeof LARGE_UPS_TABLE_COMPACT_COLUMNS)[number]
  | (typeof LARGE_UPS_TABLE_NATIVE_COMPACT_COLUMNS)[number]
  | typeof LARGE_UPS_TABLE_STATUS_COLUMN;

export const LARGE_UPS_TABLE_COLUMN_ALIGN: Record<
  LargeUpsTableColumnKey,
  'left' | 'center'
> = {
  NAME: 'left',
  STATUS: 'left',
  MODEL: 'center',
  RUNTIME: 'center',
  REDUNDANCY: 'center',
  EXISTING: 'center',
  ROOM: 'left',
};

export type LargeUpsListSection = 'existing' | 'new';

// ---------------------------------------------------------------------------
// Section-agnostic core (delegated to the shared repeatable-section factory)
// ---------------------------------------------------------------------------

const core = createRepeatableSectionListUtils<
  LargeUpsConfiguration,
  UpdateLargeUpsConfigurationInput
>({
  defaultConfiguration: DEFAULT_LARGE_UPS_CONFIGURATION,
  generateAutoName: generateAutoLargeUpsName,
  isAutoGeneratedName: isAutoGeneratedLargeUpsName,
  namePrefix: 'Large UPS ',
  newLabel: 'New large UPS',
  nameNoun: 'Large UPS',
  getDisplayNameFallback: unit =>
    unit.powerLargeUpsDescription?.trim() || undefined,
  legacySourceKeys: [
    // #1149 Issue 4: readiness/site-level fields (existing-site-protected,
    // model-of-existing UPS, facility redundancy level) are not units — keep
    // them out of the synthesis trigger so a bare readiness answer doesn't
    // conjure a phantom "0 units added" row. They still map onto a synthesized
    // unit via mapLegacyFields below when real unit data is present.
    'powerLargeUpsDescription',
    'powerRequiredRedundancyForUps',
    'powerBatteryChemistry',
    'powerBatteryRuntimeRequiredMin',
  ],
  mapLegacyFields: source => ({
    powerExistingSiteProtectedByLargeKwUps:
      source.powerExistingSiteProtectedByLargeKwUps as string | undefined,
    powerLargeUpsDescription: source.powerLargeUpsDescription as
      | string
      | undefined,
    powerModelOfExistingUps: source.powerModelOfExistingUps as
      | string
      | undefined,
    powerLevelOfRedundancy: source.powerLevelOfRedundancy as string | undefined,
    powerRequiredRedundancyForUps: source.powerRequiredRedundancyForUps as
      | string
      | undefined,
    powerBatteryChemistry: source.powerBatteryChemistry as string | undefined,
    powerBatteryRuntimeRequiredMin: source.powerBatteryRuntimeRequiredMin as
      | string
      | undefined,
  }),
});

export function normalizeLargeUpsStatus(
  status: LargeUpsConfiguration['status'] | string | undefined,
): LargeUpsConfiguration['status'] {
  return core.normalizeStatus(status);
}

export function groupLargeUpsByStatus(
  units: LargeUpsConfiguration[],
): GroupedLargeUpsConfigurations {
  return core.groupByStatus(units);
}

export function sortLargeUpsByDisplayOrder(
  units: LargeUpsConfiguration[],
): LargeUpsConfiguration[] {
  return core.sortByDisplayOrder(units);
}

/** Reorder units within EXISTING or NEW section; reassigns global displayOrder. */
export function applyLargeUpsSectionReorder(
  sortedUnits: LargeUpsConfiguration[],
  section: LargeUpsListSection,
  fromIndex: number,
  toIndex: number,
): LargeUpsConfiguration[] {
  return core.applySectionReorder(sortedUnits, section, fromIndex, toIndex);
}

/** Merge editor save payload onto a session unit without wiping fields with `undefined`. */
export function applyLargeUpsSessionPatch(
  unit: LargeUpsConfiguration,
  patch: UpdateLargeUpsConfigurationInput,
): LargeUpsConfiguration {
  return core.applySessionPatch(unit, patch);
}

export function largeUpsIdsEqual(
  left: number | undefined,
  right: number | undefined,
): boolean {
  return core.idsEqual(left, right);
}

export function getLargeUpsDescriptionDisplay(
  unit: LargeUpsConfiguration,
): string {
  return unit.powerLargeUpsDescription?.trim() || '—';
}

/** NAME column primary — user-defined name, else "Large UPS N" by display order. */
export function getLargeUpsNamePrimaryDisplay(
  unit: LargeUpsConfiguration,
): string {
  return core.getNamePrimaryDisplay(unit);
}

/** Editor chrome title — user name, else "Large UPS N", else "New large UPS". */
export function getLargeUpsEditChromeTitle(
  name: string | undefined,
  options: {
    isNew?: boolean;
    displayOrder?: number;
    positionIndex?: number;
  } = {},
): string {
  return core.getEditChromeTitle(name, options);
}

export function formatLargeUpsTableCell(
  unit: LargeUpsConfiguration,
  column: LargeUpsTableColumnKey,
): string {
  switch (column) {
    case 'NAME':
      return getLargeUpsNamePrimaryDisplay(unit);
    case 'MODEL':
      return unit.powerModelOfExistingUps?.trim() || '—';
    case 'RUNTIME':
      return unit.powerBatteryRuntimeRequiredMin?.trim() || '—';
    case 'REDUNDANCY':
      return unit.powerRequiredRedundancyForUps?.trim() || '—';
    case 'EXISTING':
      return unit.powerExistingSiteProtectedByLargeKwUps?.trim() || '—';
    case 'ROOM':
      // Rendered via LocationRoomCell in the row; text unused.
      return '—';
    case 'STATUS':
      // Rendered via RepeatableUnitStatusPill in the row.
      return '';
    default:
      return '—';
  }
}

export function largeUpsTableColumnsForLayout(
  layout: LargeUpsTableLayout,
  options?: RepeatableLocationTagColumnOptions,
): readonly LargeUpsTableColumnKey[] {
  const columns =
    layout === 'compact'
      ? LARGE_UPS_TABLE_NATIVE_COMPACT_COLUMNS
      : LARGE_UPS_TABLE_DESKTOP_COLUMNS;
  return filterRepeatableLocationTagTableColumns(columns, options, layout);
}

export function largeUpsTableHeaderLabel(
  column: LargeUpsTableColumnKey,
  layout: LargeUpsTableLayout,
): string {
  if (layout === 'compact') {
    if (column === 'MODEL') {
      return formatRepeatableLocationTagColumnHeader(column, 'MODEL');
    }
    // Compact-only trailing metric — short header fits the fixed narrow trailing
    // column (#999). Applies to the shared compact layout (native + mobile web).
    if (column === 'RUNTIME') {
      return formatRepeatableLocationTagColumnHeader(column, 'RUN');
    }
  }
  return formatRepeatableLocationTagColumnHeader(column, column);
}

/** DESCRIPTION subline — battery chemistry when set. */
export function getLargeUpsBatterySubline(
  unit: LargeUpsConfiguration,
): string | undefined {
  const chemistry = unit.powerBatteryChemistry?.trim();
  return chemistry || undefined;
}

/** NAME subline (compact) — required redundancy. */
export function getLargeUpsRedundancySubline(
  unit: LargeUpsConfiguration,
): string | undefined {
  const redundancy = unit.powerRequiredRedundancyForUps?.trim();
  return redundancy || undefined;
}

export function getLargeUpsColumnSubline(
  column: LargeUpsTableColumnKey,
  unit: LargeUpsConfiguration,
  layout: LargeUpsTableLayout,
  options?: { locationTagName?: string | null },
): string | undefined {
  // Native compact (#999 Option B): STATUS pill folds under NAME, the battery
  // subline carries a `Battery:` label on MODEL, and redundancy relocates (bare)
  // under the trailing RUNTIME cell.
  if (isNativeCompactLayout(layout)) {
    if (column === 'MODEL') {
      const chemistry = unit.powerBatteryChemistry?.trim();
      return chemistry ? `Battery: ${chemistry}` : undefined;
    }
    if (column === 'RUNTIME') {
      return getLargeUpsRedundancySubline(unit);
    }
    return undefined;
  }
  if (column === 'NAME') {
    return buildRepeatableNameSubline(layout, {
      compactParts:
        layout === 'compact' ? [getLargeUpsRedundancySubline(unit)] : undefined,
      locationTagName: options?.locationTagName,
    });
  }
  if (column === 'MODEL') {
    return getLargeUpsBatterySubline(unit);
  }
  return undefined;
}

export function largeUpsColumnUsesDoubleLine(
  column: LargeUpsTableColumnKey,
  layout: LargeUpsTableLayout = 'desktop',
  options?: { locationTagName?: string | null },
): boolean {
  if (isNativeCompactLayout(layout)) {
    // NAME is single-line (pill renders beneath it in the row); MODEL (middle)
    // and RUNTIME (trailing) double-line their relocated sublines.
    return column === 'MODEL' || column === 'RUNTIME';
  }
  if (column === 'NAME') {
    return repeatableNameColumnUsesDoubleLine(layout, options?.locationTagName);
  }
  return column === 'MODEL';
}

/**
 * Primary list title — user-defined name preferred, then description, then
 * "Large UPS N" when displayOrder is available. Returns null when no suitable name.
 */
export function getLargeUpsDisplayName(
  unit: LargeUpsConfiguration,
): string | null {
  return core.getDisplayName(unit);
}

/** Unique duplicate name — user names get `-copy`; unnamed/auto names get a fresh serial. */
export function buildDuplicateLargeUpsName(
  sourceName: string | undefined,
  takenNames: Iterable<string | undefined> = [],
  tempId: number,
): string {
  return core.buildDuplicateName(sourceName, takenNames, tempId);
}

/** Assign a serial name when the user did not provide one. */
export function ensureLargeUpsNameForCreate(
  data: UpdateLargeUpsConfigurationInput,
  tempId: number,
  takenNames: Iterable<string | undefined> = [],
): UpdateLargeUpsConfigurationInput {
  return core.ensureNameForCreate(data, tempId, takenNames);
}

/** Client-side uniqueness check before persisting large UPS session changes. */
export function validateSessionLargeUpsNames(
  session: LargeUpsConfiguration[],
): string | null {
  return core.validateSessionNames(session);
}

/** Clone fields for duplicate flow (no id/metadata; keeps source existing/new status). */
export function cloneLargeUpsForCreate(
  source: LargeUpsConfiguration,
  takenNames: Iterable<string | undefined> = [],
  tempId: number,
): Partial<LargeUpsConfiguration> {
  return core.cloneForCreate(source, takenNames, tempId);
}

/** Parse numeric form input — returns undefined for empty or non-finite values. */
export const parseOptionalNumber = parseOptionalNumberCore;

export function createDraftLargeUpsConfiguration(
  data: UpdateLargeUpsConfigurationInput,
  displayOrder: number,
  tempId: number,
): LargeUpsConfiguration {
  return core.createDraftConfiguration(data, displayOrder, tempId);
}

/** Client-only id for a unit synthesized from legacy flat request columns. */
export const LEGACY_LARGE_UPS_SYNTHETIC_ID = core.SYNTHETIC_ID;

/** Whether a list row may offer delete (draft rows or persisted id > 0, not legacy synthetic). */
export function canDeleteLargeUpsFromList(
  unit: LargeUpsConfiguration,
  isDraftMode: boolean,
): boolean {
  return core.canDeleteFromList(unit, isDraftMode);
}

export function hasLegacyLargeUpsRequestFields(
  source: Record<string, unknown>,
): boolean {
  return core.hasLegacyRequestFields(source);
}

/** Build one display row from pre–multi-instance flat request fields when child rows are absent. */
export function buildLegacyLargeUpsFromRequestFields(
  source: Record<string, unknown>,
  requestId: string | number,
): LargeUpsConfiguration | null {
  return core.buildLegacyFromRequestFields(source, requestId);
}
